import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as forgerock from '@forgerock/javascript-sdk';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  BackendAuthResponse,
  BackendTokenPayload,
  PreviewTokenExchangeResponse,
} from '../interfaces/auth.interfaces';
import { LoginService } from './login.service';

type StoredPingTokens = forgerock.OAuth2Tokens & {
  accessToken: string;
  refreshToken: string;
};

type PingWellKnownResponse = {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint: string;
  revocation_endpoint: string;
  end_session_endpoint: string;
  ping_end_idp_session_endpoint?: string;
  ping_end_session_endpoint?: string;
};

type ForgeRockServerConfig = {
  baseUrl: string;
  timeout: number;
  paths: {
    authorize: string;
    accessToken: string;
    endSession: string;
    userInfo: string;
    revoke: string;
  };
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public user: BackendTokenPayload | null = null;
  public loggedIn = new BehaviorSubject<boolean>(false);
  public configInitialised = false;
  public groups: any[] = [];
  public organisations: any[] = [];
  private refreshPromise: Promise<boolean> | null = null;
  private loggingOut = false;

  constructor(
    public readonly jwtHelper: JwtHelperService,
    private readonly loginService: LoginService,
    private readonly router: Router,
  ) {}

  public async configInit(): Promise<void> {
    if (environment.verboseLogging) {
      console.log('Setting up Ping configuration...');
    }
    try {
      const redirectUri =
        environment.AuthConfig.redirectUri ||
        `${globalThis.location.origin}/callback`;

      const serverConfig = await this.getServerConfig(
        environment.AuthConfig.discoveryUrl,
      );

      forgerock.Config.set({
        clientId: `${environment.AuthConfig.clientId}`,
        redirectUri,
        scope: `${environment.AuthConfig.scope}`,
        serverConfig,
        tokenStore: 'localStorage',
      });

      this.configInitialised = true;
      if (environment.verboseLogging) {
        console.log('Ping config initialised');
      }

      await this.checkExistingAuth();
    } catch (error) {
      if (environment.verboseLogging) {
        console.error('Failed to initialise ForgeRock config:', error);
      }
      throw error;
    }
  }

  private async getServerConfig(
    discoveryUrl: string,
  ): Promise<ForgeRockServerConfig> {
    const response = await fetch(discoveryUrl);

    if (!response.ok) {
      throw new Error(
        `Failed to load Ping discovery document: ${response.status}`,
      );
    }

    const wellKnown = (await response.json()) as PingWellKnownResponse;
    const endSessionUrl =
      wellKnown.ping_end_session_endpoint ||
      wellKnown.ping_end_idp_session_endpoint ||
      wellKnown.end_session_endpoint;

    return {
      baseUrl: new URL(wellKnown.authorization_endpoint).origin,
      timeout: 5000,
      paths: {
        authorize: new URL(wellKnown.authorization_endpoint).pathname,
        accessToken: new URL(wellKnown.token_endpoint).pathname,
        endSession: new URL(endSessionUrl).pathname,
        userInfo: new URL(wellKnown.userinfo_endpoint).pathname,
        revoke: new URL(wellKnown.revocation_endpoint).pathname,
      },
    };
  }

  private async checkExistingAuth(): Promise<void> {
    try {
      const token = localStorage.getItem('partnerchoice_authentication');

      if (!token) {
        if (environment.verboseLogging) {
          console.log('No existing authentication found');
        }
        this.loggedIn.next(false);
        return;
      }

      if (this.jwtHelper.isTokenExpired(token)) {
        if (environment.verboseLogging) {
          console.log('Existing token expired, clearing auth');
        }
        await this.clearSession();
        return;
      }

      this.restoreSession(token);
    } catch (error) {
      if (environment.verboseLogging) {
        console.error('Error checking existing auth:', error);
      }
      await this.clearSession();
    }
  }

  private async clearSession(): Promise<void> {
    localStorage.removeItem('partnerchoice_authentication');
    localStorage.removeItem('partnerchoice_authentication_expires_at');
    this.user = null;
    this.groups = [];
    this.organisations = [];
    try {
      await forgerock.TokenStorage.remove();
    } catch (error) {
      if (environment.verboseLogging) {
        console.error('error clearing localStorage: ', error);
      }
    }
    this.loggedIn.next(false);
  }

  async login() {
    if (!this.configInitialised) {
      throw new Error('Authentication system not initialised');
    }

    try {
      await this.checkExistingAuth();
      if (this.loggedIn.value) {
        await this.router.navigate(['/dashboard/admin']);
        return;
      }

      await forgerock.TokenStorage.remove();

      await forgerock.TokenManager.getTokens({
        login: 'redirect',
        forceRenew: true,
        skipBackgroundRequest: true,
        query: { prompt: 'login' },
      });
      if (environment.verboseLogging) {
        console.log('Redirecting to Ping login...');
      }
    } catch (error) {
      if (environment.verboseLogging) {
        console.log('Login failed:', error);
      }
      throw error;
    }
  }

  async handleCallback(): Promise<boolean> {
    if (!this.configInitialised) {
      if (environment.verboseLogging) {
        console.error('Config not initialised in callback');
      }
      this.router.navigate(['/login'], {
        state: { error: 'Authentication system not ready' },
      });
      return false;
    }

    try {
      if (environment.verboseLogging) {
        console.log('Handling OAuth callback...');
      }

      const url = new URL(document.location.href);
      const params = url.searchParams;
      const code = params.get('code');
      const state = params.get('state');

      if (!code || !state) {
        throw new Error('Missing authorization code or state');
      }

      const pingTokens = await forgerock.TokenManager.getTokens({
        query: { code, state },
      });

      if (environment.verboseLogging) {
        console.log('ping tokens: ', pingTokens);
      }
      if (!pingTokens || !(pingTokens as forgerock.OAuth2Tokens).idToken) {
        throw new Error('Failed to obtain tokens from PingFederate');
      }
      if (environment.verboseLogging) {
        console.log('Ping tokens obtained successfully');
      }

      const loginRequestBody = {
        admin: true,
        ...((pingTokens as forgerock.OAuth2Tokens).accessToken
          ? { accessToken: (pingTokens as forgerock.OAuth2Tokens).accessToken }
          : {}),
      };

      const backendResponse = (await this.loginService.postWithPingAuth(
        'login',
        (pingTokens as forgerock.OAuth2Tokens).idToken,
        loginRequestBody,
      )) as BackendAuthResponse;
      if (environment.verboseLogging) {
        console.log('Backend authentication successful');
      }

      this.setSession(backendResponse);

      this.router.navigate(['/dashboard/admin']);

      return true;
    } catch (error) {
      if (environment.verboseLogging) {
        console.error('Callback handling failed:', error);
      }

      await this.clearSession();
      throw error;
    }
  }

  private setSession(authResult: BackendAuthResponse): void {
    const { token } = authResult;

    const tokenData = this.jwtHelper.decodeToken<BackendTokenPayload>(token);

    localStorage.setItem('partnerchoice_authentication', token);
    localStorage.setItem(
      'partnerchoice_authentication_expires_at',
      JSON.stringify(tokenData.exp * 1000),
    );

    this.user = tokenData;
    this.groups = tokenData.groups || [];
    this.organisations = tokenData.organisations || [];

    this.loggedIn.next(true);
  }

  async logout() {
    this.loggingOut = true;
    let logoutRedirectStarted = false;

    try {
      await forgerock.FRUser.logout({
        logoutRedirectUri: `${globalThis.location.origin}/login`,
      });
      logoutRedirectStarted = true;
    } catch (error) {
      if (environment.verboseLogging) {
        console.error('Logout failed:', error);
      }
    } finally {
      await this.clearSession();

      if (!logoutRedirectStarted) {
        this.loggingOut = false;
        await this.router.navigate(['/login']);
      }
    }
  }

  public isLoggingOut(): boolean {
    return this.loggingOut;
  }

  async handleUnauthorized(): Promise<void> {
    await this.clearSession();
    await this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem('partnerchoice_authentication');
  }

  async getPingAccessToken(): Promise<string | null> {
    const pingTokens = await forgerock.TokenStorage.get();

    if (!this.hasAccessToken(pingTokens)) {
      return null;
    }

    return pingTokens.accessToken;
  }

  async exchangePreviewTokens(): Promise<PreviewTokenExchangeResponse | null> {
    const tokenValid = await this.refreshToken();

    if (!tokenValid) {
      return null;
    }

    const pingAccessToken = await this.getPingAccessToken();

    if (!pingAccessToken) {
      return null;
    }

    return this.loginService.postWithPingAccessToken(
      'preview-token/exchange',
      pingAccessToken,
    ) as Promise<PreviewTokenExchangeResponse>;
  }

  async refreshToken(): Promise<boolean> {
    if (this.refreshPromise !== null) {
      if (environment.verboseLogging) {
        console.log('Token refresh already in progress, waiting...');
      }
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      const token = this.getToken();

      if (!token) {
        if (environment.verboseLogging) {
          console.log('No token found, re-authentication required');
        }
        return false;
      }

      const pingTokens = await this.getStoredPingTokens();
      if (pingTokens === null) {
        return false;
      }

      if (!this.isPingAccessTokenExpiringSoon(
        pingTokens.accessToken,
        pingTokens.tokenExpiry,
      )) {
        if (environment.verboseLogging) {
          console.log('Ping access token still valid, no refresh needed');
        }
        return true;
      }

      const tokensToStore = await this.refreshStoredPingTokens(pingTokens);
      if (tokensToStore === null) {
        return false;
      }

      const response = (await this.loginService.postWithPingAccessToken(
        'admin/auth/ping-refresh',
        tokensToStore.accessToken,
      )) as BackendAuthResponse;

      if (environment.verboseLogging) {
        console.log('Backend token refreshed with Ping access token');
      }

      this.setSession(response);

      return true;
    } catch (error) {
      if (environment.verboseLogging) {
        console.error('Token refresh failed:', error);
      }
      await this.clearSession();
      return false;
    }
  }

  private restoreSession(token: string): void {
    if (environment.verboseLogging) {
      console.log('Found valid existing authentication');
    }

    const tokenData = this.jwtHelper.decodeToken<BackendTokenPayload>(token);
    this.user = tokenData;
    this.groups = tokenData.groups || [];
    this.organisations = tokenData.organisations || [];
    this.loggedIn.next(true);
  }

  private async getStoredPingTokens(): Promise<StoredPingTokens | null> {
    const pingTokens = await forgerock.TokenStorage.get();

    if (!this.hasAccessToken(pingTokens)) {
      if (environment.verboseLogging) {
        console.log('No Ping tokens found, re-authentication required');
      }
      await this.clearSession();
      return null;
    }

    if (!this.hasRefreshToken(pingTokens)) {
      if (environment.verboseLogging) {
        console.log('No refresh token found, re-authentication required');
      }
      await this.clearSession();
      return null;
    }

    return {
      ...pingTokens,
      accessToken: pingTokens.accessToken,
      refreshToken: pingTokens.refreshToken,
    };
  }

  private hasAccessToken(
    pingTokens: void | forgerock.Tokens,
  ): pingTokens is forgerock.OAuth2Tokens & { accessToken: string } {
    return !!pingTokens && 'accessToken' in pingTokens && !!pingTokens.accessToken;
  }

  private hasRefreshToken(
    pingTokens: forgerock.OAuth2Tokens & { accessToken: string },
  ): pingTokens is StoredPingTokens {
    return 'refreshToken' in pingTokens && !!pingTokens.refreshToken;
  }

  private async refreshStoredPingTokens(
    pingTokens: StoredPingTokens,
  ): Promise<forgerock.OAuth2Tokens | null> {
    if (environment.verboseLogging) {
      console.log(
        'Ping access token expiring soon, refreshing using refresh token...',
      );
    }

    const refreshedPingTokens = await this.loginService.refreshPingTokens(
      pingTokens.refreshToken,
    );

    if (!refreshedPingTokens) {
      if (environment.verboseLogging) {
        console.log(
          'Failed to refresh Ping tokens, re-authentication required',
        );
      }
      await this.clearSession();
      return null;
    }

    const tokensToStore: forgerock.OAuth2Tokens = {
      ...pingTokens,
      ...refreshedPingTokens,
      idToken: refreshedPingTokens.idToken || pingTokens.idToken,
    };

    if (environment.verboseLogging) {
      console.log('Ping tokens refreshed successfully');
    }

    await forgerock.TokenStorage.set(tokensToStore);

    return tokensToStore;
  }

  getLoggedInUser(): BackendTokenPayload | null {
    return this.user;
  }

  private isPingAccessTokenExpiringSoon(
    accessToken: string,
    tokenExpiry: number | undefined,
  ): boolean {
    if (tokenExpiry) {
      return tokenExpiry <= Date.now() + 900 * 1000;
    }

    try {
      return this.jwtHelper.isTokenExpired(accessToken, 900);
    } catch {
      return true;
    }
  }

  public inGroup(group: string): boolean {
    if (!this.user || !this.groups) {
      return false;
    }

    if (group === 'listing-admin' || group === 'event-admin') {
      if (this.organisations?.length >= 1) {
        return true;
      }
    }

    return this.groups.some((g) => g.slug === group);
  }
}
